import { debugLog } from '../utils/debugLog';
import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { prefetchAllFundsPerformance } from "../utils/govApi";
import FundsTable from "./FundsTable";
import { SummaryCard } from "./UI";
import { CATEGORY_STYLE } from "../utils/fieldMaps";
import { fmtMoney } from "../parsers/xmlParser";
import { sourceMeta } from "../utils/dataSource";
import { PitzuyimModal, TagmulimModal, HonModal, KitzbaModal, AlertsModal, MoneyColorModal, KisuimModal, computeTotalDeathCover } from "./ReportModals";
import ClientDetailsModal from "./ClientDetailsModal";
import RelationshipsModal from "./RelationshipsModal";
import { useClientRelationships } from "../hooks/useClientRelationships";
import { usePlanManagement } from "../hooks/usePlanManagement";
import { computeHonKitzba } from "../utils/honKitzba";
import { aggregateClient, aggregateEmployer, aggregateAgent } from "../utils/aggregateClientData";
import { applyClientOverrides } from "../utils/clientOverrides";
import CustomerSnapshot from "./CustomerSnapshot";
import PortfolioExposure from "./PortfolioExposure";
import QuickLogModal from "./QuickLogModal";
import MonthlyReturnPanel from "./MonthlyReturnPanel";
import { useHoldingsHistory } from "../hooks/useHoldingsHistory";
import { useReportSendLog } from "../hooks/useReportSendLog";
import ManualProductModal from "./ManualProductModal";
import ClientReportHistoryModal from "./ClientReportHistoryModal";
import { TAB } from "./retirementPlanningTabs";
import ClientNotesModal from "./ClientNotesModal";

const RetirementPlanning = lazy(() => import("./RetirementPlanning"));
const Form161Modal = lazy(() => import("./Form161Modal"));
const Form116Modal = lazy(() => import("./Form116Modal"));
const KivuZchuyotModal = lazy(() => import("./KivuZchuyot"));
const KitzbaMukeret = lazy(() => import("./KitzbaMukeret"));
const DashboardCharts = lazy(() => import("./DashboardCharts"));

const RETIREMENT_BTN_STYLES = `
@keyframes rp-spin { to { transform: rotate(360deg); } }
@keyframes rp-glow { 0%,100% { box-shadow: 0 0 0 0 rgba(5,150,105,0.45); } 50% { box-shadow: 0 0 0 7px rgba(5,150,105,0); } }
`;

function RetirementSpinner() {
  return (
    <span style={{
      display: "inline-block", width: 22, height: 22,
      border: "2.5px solid #bbf7d0", borderTopColor: "#059669",
      borderRadius: "50%", animation: "rp-spin 0.65s linear infinite",
    }} />
  );
}

// פרט (הכשרה/הראל/כלל) fund ids are prefixed by source; used to split them
// from 008 funds when building the per-month unified view.
const isPropFund = f => /^(hachshara|harel|clal|menora)_/.test(f.id || "");

function ModalSpinner({ minHeight = 200 }) {
  return (
    <div style={{
      minHeight,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#94a3b8",
      fontSize: 13,
    }}>
      טוען...
    </div>
  );
}


export default function ClientDashboard({
  funds, fileInfo, clientId, clients, onLoadNew, onRemove,
  isCompareMode, onOpenSplit, onCloseSplit, onSetSlot, onLogActivity,
  logEntries, onUpdateLog, onDeleteLog, onOpenLog, onSelectClient,
  source, hasAdvice, hasHoldings, onToggleSource,
  monthlyFunds = {}, proprietaryMonths = [],  // פרט (הכשרה/הראל/כלל) months for the unified month filter
  onSwitchDataset,
  onSendPortalInvite,  // optional — shows "Send Portal" button when provided
  onAddManualFund, onUpdateManualFund, onRemoveFund,  // manual products (holdings only)
  onGoHome,            // optional — shows Home button in the header
}) {
  const [showManualModal, setShowManualModal] = useState(false);
  // Mobile-only "⋯" header menu that collects the secondary header actions
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const [filterStatus, setFilterStatus] = useState("הכל");
  const [search, setSearch] = useState("");
  const [showCharts, setShowCharts] = useState(true);
  const [retirementView, setRetirementView] = useState(false);
  const [activeTab, setActiveTab] = useState("holdings");

  // Single state for which modal is open (null = none)
  const [modal, setModal] = useState(null);
  const [showQuickLog, setShowQuickLog] = useState(false);
  const [showRelationships, setShowRelationships] = useState(false);
  const [showMonthlyReturn, setShowMonthlyReturn] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const openModal = (name) => setModal(name);
  const [returnModal, setReturnModal] = useState(null);
  // איזה טאב לפתוח כשחוזרים מ-161/קיבוע זכויות אל תוך תכנון פרישה (null = ברירת מחדל)
  const [retirementInitialTab, setRetirementInitialTab] = useState(null);
  // Persistent data for forms that survive open/close
  const [form161Data, setForm161Data] = useState(null);
  const [form116Data, setForm116Data] = useState(null);
  const [kivuData, setKivuData] = useState(null);
  const [retirementData, setRetirementData] = useState(null);
  const [retirementPending, setRetirementPending] = useState(false);

  useEffect(() => {
    if (funds?.length) prefetchAllFundsPerformance(funds);
  }, [funds]);

  useEffect(() => {
    if (clientId) {
      import('./RetirementPlanning');
      setRetirementPending(false);
    }
  }, [clientId]);

  // Reset month selection when switching client or source
  useEffect(() => { setSelectedMonth(null); }, [clientId, source]);

  useEffect(() => {
    if (modal !== "retirement") setRetirementPending(false);
  }, [modal]);

  const handleRetirementClick = () => {
    setRetirementPending(true);
    setRetirementInitialTab(null); // פתיחה טרייה — לא חזרה מ-161/קיבוע זכויות
    if (isCompareMode) setRetirementView(true);
    else openModal("retirement");
  };

  const plan = usePlanManagement(clientId);
  const { relations } = useClientRelationships(clientId);


  const clientTaz = clients?.find(c => c.id === clientId)?.clientId || clientId;
  const history = useHoldingsHistory(clientTaz);

  // Report send history for this client (bulk + single sends) — powers the
  // "🕐 דוחות" header button; hidden when the client never received a report.
  const { log: reportLog } = useReportSendLog();
  const sentReportsCount = useMemo(() => {
    const e = reportLog[clientTaz];
    if (!e) return 0;
    return (e.history?.length) || (e.lastSentAt ? 1 : 0);
  }, [reportLog, clientTaz]);

  // Unified month options across all holdings sources: 008 snapshots + פרט months
  // (הכשרה/הראל/כלל). Descending (newest first).
  const monthOptions = useMemo(() => {
    const set = new Set([...history.map(s => s.period), ...(proprietaryMonths || [])]);
    return [...set].sort().reverse();
  }, [history, proprietaryMonths]);

  // Monthly snapshots for פרט (הכשרה/הראל/כלל) → feeds MonthlyReturnPanel when the
  // client has no 008 history. Files carry balances only (no flows), so returns are
  // gross-approximate (month-over-month balance change), flagged via _approxReturn.
  const proprietaryHistory = useMemo(() => {
    const periods = Object.keys(monthlyFunds || {}).sort(); // ascending
    let prev = null;
    return periods.map(period => {
      const arr = monthlyFunds[period] || [];
      const perFund = arr.map(f => ({
        key: f.id,
        companyName: f.companyName,
        polNum: f.polNum,
        balance: f._month?.balance ?? f.redeem ?? 0,
        deposits: 0,
        withdrawals: 0,
        ret: null,
        dmeiNihulTzv: null,
        dmeiNihulHaf: null,
        maslulim: f._month?.maslulim || f.maslulim || [],
      }));
      const totalBalance = perFund.reduce((s, p) => s + (p.balance || 0), 0);
      const snap = {
        period,
        valueDate: arr[0]?._month?.valueDate || `${period}-01`,
        totalBalance, totalDeposits: 0, totalWithdrawals: 0, ret: null,
        perFund, _approxReturn: true,
      };
      if (prev) {
        const prevByKey = new Map(prev.perFund.map(p => [p.key, p]));
        for (const p of perFund) {
          const pp = prevByKey.get(p.key);
          p.ret = pp && pp.balance > 0 ? (p.balance - pp.balance) / pp.balance : null;
        }
        snap.ret = prev.totalBalance > 0 ? (totalBalance - prev.totalBalance) / prev.totalBalance : null;
      }
      prev = snap;
      return snap;
    });
  }, [monthlyFunds]);

  // When a month is chosen, build ONE unified list combining every holdings source
  // for that month: 008 funds (balances overridden from the snapshot) + the פרט
  // funds uploaded for that month. Funds with no data for the month drop out.
  const activeMonth = selectedMonth || (monthOptions.length > 0 ? monthOptions[0] : null);

  // 008 snapshot for the chosen month (if any). Keys match holdingsHistory.fundKey.
  const selectedSnapshot = useMemo(
    () => (activeMonth ? history.find(s => s.period === activeMonth) || null : null),
    [history, activeMonth]
  );
  const activeClient = useMemo(() => clients?.find(c => c.id === clientId) || null, [clients, clientId]);

  const historicalDataset = useMemo(() => {
    if (!activeMonth || !activeClient?.datasets) {
       debugLog("[DEBUG] historicalDataset skip", { activeMonth, hasDatasets: !!activeClient?.datasets });
       return null;
    }
    debugLog("[DEBUG] checking datasets against activeMonth:", activeMonth);
    return activeClient.datasets.find((ds, i) => {
      let iso = null;
      if (ds.fileInfo?.taarichBitzua) {
        const m = ds.fileInfo.taarichBitzua.match(/(\d{2})\/(\d{2})\/(\d{4})/);
        if (m) iso = `${m[3]}-${m[2]}`;
      }
      if (!iso && ds.funds) {
        let best = null;
        for (const f of ds.funds) {
          const d = f.yitrot?.date;
          if (!d) continue;
          const m = String(d).match(/(\d{2})\/(\d{2})\/(\d{4})/);
          const v = m ? `${m[3]}-${m[2]}` : null;
          if (v && (!best || v > best)) best = v;
        }
        iso = best;
      }
      debugLog(`[DEBUG] dataset ${i} taarichBitzua=${ds.fileInfo?.taarichBitzua} iso=${iso}`);
      return iso === activeMonth;
    });
  }, [activeMonth, activeClient]);

  const fundsForView = useMemo(() => {
    if (source !== "holdings" || !activeMonth) return funds;

    const strict = !!selectedMonth;
    let list = funds.filter(f => !isPropFund(f));
    if (selectedSnapshot) {
      const liveKey = f => f.id || `${f.companyCode || ""}_${f.polNum || ""}_${f.kidodAchid || ""}`;
      const byKey = new Map(selectedSnapshot.perFund.map(pf => [pf.key, pf]));
      
      const historicalFundsByKey = new Map();
      if (historicalDataset?.funds) {
        debugLog(`[DEBUG] Found historical dataset with ${historicalDataset.funds.length} funds`);
        historicalDataset.funds.forEach(hf => historicalFundsByKey.set(liveKey(hf), hf));
      } else {
        debugLog(`[DEBUG] NO historical dataset found for month ${activeMonth}`);
      }

      list = list.map(f => {
        const pf = byKey.get(liveKey(f));
        if (!pf) return f;
        
        const baseFund = historicalFundsByKey.get(liveKey(f)) || f;
        if (historicalFundsByKey.has(liveKey(f))) {
           debugLog(`[DEBUG] Successfully applied historical baseFund for ${liveKey(f)}`);
        }

        return {
          ...baseFund,
          ...pf, // Includes all historical fields we now save (...f)
          id: baseFund.id, // Preserve identity
          redeem: pf.balance, // Explicitly override redeem with snapshot balance
          validDate: selectedSnapshot.valueDate ? selectedSnapshot.valueDate.split('-').reverse().join('/') : baseFund.validDate,
          maslulim: pf.maslulim || baseFund.maslulim,
          dmeiNihul: {
            ...baseFund.dmeiNihul,
            actual: {
              ...(baseFund.dmeiNihul?.actual || {}),
              dmeiTzvPct: pf.dmeiNihulTzv ?? baseFund.dmeiNihul?.actual?.dmeiTzvPct,
              dmeiHafPct: pf.dmeiNihulHaf ?? baseFund.dmeiNihul?.actual?.dmeiHafPct,
            }
          },
          _month: {
            period: selectedSnapshot.period,
            valueDate: selectedSnapshot.valueDate,
            balance: pf.balance,
            deposits: pf.deposits,
            withdrawals: pf.withdrawals,
            ret: pf.ret,
            dmeiNihulTzv: pf.dmeiNihulTzv,
            dmeiNihulHaf: pf.dmeiNihulHaf,
            maslulim: pf.maslulim,
            flows: pf.flows,
          },
        };
      });
      if (strict) list = list.filter(f => f._month); // חודש מפורש → רק קרנות מה-snapshot
    } else if (strict) {
      // חודש מפורש בלי snapshot של 008 → אין קרנות 008 להציג.
      list = [];
    }
    // ברירת מחדל בלי snapshot → משאירים את כל קרנות ה-008 בנתונים המקוריים.

    // פרט part: the funds uploaded for the selected month (already carry _month).
    const propFunds = monthlyFunds?.[activeMonth] || [];

    const merged = [...list, ...propFunds];
    return merged.length ? merged : funds; // fallback if nothing lined up
  }, [funds, source, activeMonth, selectedSnapshot, monthlyFunds]);

  const filtered = useMemo(() => {
    let list = fundsForView;

    if (activeTab === "holdings") {
      list = list.filter(f => f.cat !== "בריאות" && f.cat !== "חיים");
    } else if (activeTab === "insurance") {
      list = list.filter(f => f.cat === "בריאות" || f.cat === "חיים");
    }

    const res = list.filter(f => {
      const matchStatus = filterStatus === "הכל" || f.statusLabel === filterStatus;
      const q = search.trim();
      const matchSearch = !q || [f.companyName, f.tochnit, f.polNum, f.client?.name]
        .some(v => v?.includes(q));
      return matchStatus && matchSearch;
    });
    return res.sort((a, b) => (b.redeem || 0) - (a.redeem || 0));
  }, [fundsForView, filterStatus, search, activeTab]);

  // Summary KPIs reflect the selected month (fundsForView), per "הכל" request.
  const totals = useMemo(() => {
    let tagmulimHon = 0, tagmulimKitzba = 0, pitzuyimKitzba = 0;
    fundsForView.forEach(f => {
      const { hon, kitzba, kitzbaPitzuyim, preReform, kitzbaPreSettlement } = computeHonKitzba(f);
      const kitzbaUntil2000 = (preReform || 0) + (kitzbaPreSettlement || 0);

      tagmulimHon    += hon + kitzbaUntil2000;
      tagmulimKitzba += (kitzba || 0) - (kitzbaPreSettlement || 0);
      pitzuyimKitzba += kitzbaPitzuyim || 0;
    });
    const pitzuyimTotal = fundsForView
      .filter(f => f.yitrot?.pitzuyim?.erkeiPitzuimMaasik || f.yitrot?.perut?.some(p => p.sugHafrasha === "פיצויים"))
      .reduce((s, f) => s + (f.yitrot?.pitzuyim?.erkeiPitzuimMaasik ||
        f.yitrot?.perut?.filter(p => p.sugHafrasha === "פיצויים").reduce((a, p) => a + (p.redeem || 0), 0) || 0), 0);
    const tagmulimTotal = fundsForView
      .filter(f => f.cat !== "קרן השתלמות")
      .reduce((s, f) => s + (f.yitrot?.perut || [])
        .filter(p => p.sugHafrasha !== "פיצויים")
        .reduce((a, p) => a + (p.redeem || 0), 0), 0);
    return {
      redeem:  fundsForView.reduce((s, f) => s + (f.redeem || 0), 0),
      pension: fundsForView.reduce((s, f) => {
        const p = f.retirementProjections?.[0]?.kupot?.[0]?.pensionMonthly
               || f.retirementProjections?.[0]?.kupot?.[0]?.pension || 0;
        return s + (parseFloat(p) || 0);
      }, 0),
      // כיסוי מוות כולל — על פני כל הכיסויים בכל המוצרים (כולל מטריות), לא רק הכיסוי הראשון
      bituach: computeTotalDeathCover(fundsForView),
      alerts: fundsForView.filter(f => f.debts || f.hasShiabud || f.loan).length,
      tagmulimHon,
      tagmulimKitzba,
      pitzuyimKitzba,
      pitzuyimTotal,
      tagmulimTotal,
    };
  }, [fundsForView]); // eslint-disable-line

  const statuses = useMemo(() => ["הכל", ...new Set(fundsForView.map(f => f.statusLabel || "פעיל"))], [fundsForView]);

  // נתוני המקור מהקבצים בלבד — משמש את מודל פרטי הלקוח (בסיס לעריכה)
  const aggregatedClientBase = useMemo(() => aggregateClient(funds), [funds]);
  // עריכות ידניות (clientOverrides) ממוזגות מעל — overridesVersion מרענן אחרי שמירה
  const [overridesVersion, setOverridesVersion] = useState(0);
  const aggregatedClient = useMemo(
    () => applyClientOverrides(aggregatedClientBase, fileInfo?.clientId),
    [aggregatedClientBase, fileInfo?.clientId, overridesVersion]
  );
  const aggregatedEmployer = useMemo(() => aggregateEmployer(funds), [funds]);
  const aggregatedAgent    = useMemo(() => aggregateAgent(funds), [funds]);

  const reportGridCols = isCompareMode ? "repeat(2,1fr)" : `repeat(auto-fill, minmax(${isMobile ? "140px" : "180px"}, 1fr))`;

  const retirementProps = {
    funds,
    clients,
    clientId,
    clientName: fileInfo?.client,
    plans: plan.plans,
    activePlanId: plan.activePlanId,
    planKey: plan.planKey,
    initialData: plan.loadedPlanData,
    snapshotRef: plan.snapshotRef,
    onTabNotesChange: plan.handleTabNotesChange,
    onSectionNotesChange: plan.handleSectionNotesChange,
    onPlanningData: setRetirementData,
    onSavePlan: plan.handleSavePlan,
    onUpdatePlan: plan.handleUpdatePlan,
    onDeletePlan: plan.handleDeletePlan,
    onLoadPlan: plan.handleLoadPlan,
    initialTab: retirementInitialTab,
    onOpen161: () => { setReturnModal(modal); setRetirementInitialTab(TAB.SEVERANCE); openModal("161"); },
    onOpen116: () => openModal("116"),
    onOpenKivuZchuyot: () => { setReturnModal(modal); setRetirementInitialTab(TAB.PENSIONS); openModal("kivuZchuyot"); },
    onLogActivity,
    onOpenLog,
  };

  return (
    <div dir="rtl" style={{ position: "relative", minHeight: "100vh", background: "#f1f5f9", fontFamily: "inherit", flexShrink: 0 }}>

      {/* Client header bar — sticky */}
      <div style={{
        background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
        color: "white", padding: "16px 16px", display: "flex",
        justifyContent: "center", alignItems: "center",
        flexShrink: 0,
        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ position: "relative", width: "100%" }} className={isMobile ? "mobile-hamburger-offset" : ""}>
          {/* Outer: name+buttons on the left, big home on the right aligned to bottom */}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Row 1: client name */}
              <div 
                onClick={() => setModal("clientDetails")}
                title="לחץ לצפייה בפרטי לקוח"
                style={{ 
                  fontWeight: 800, 
                  fontSize: isMobile ? 18 : isCompareMode ? 20 : 24, 
                  whiteSpace: "nowrap", 
                  marginBottom: 6,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  textDecoration: "underline",
                  textUnderlineOffset: "4px",
                  textDecorationColor: "rgba(255,255,255,0.4)"
                }}
              >
                {fileInfo?.client || "לקוח"}
                <span style={{ fontSize: 16, opacity: 0.8 }}>👤</span>
              </div>

              {/* Row 2: ת"ז · products + all action buttons */}
              <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, opacity: 0.85, whiteSpace: "nowrap", marginLeft: 6 }}>
                  {fileInfo?.clientId} · {fileInfo?.fundsCount} מוצרים
                </span>

                {/* סיכום מקורות המידע של הקרנות המוצגות */}
                {[...new Set((funds || []).map(f => f._sourceType).filter(Boolean))].map(st => {
                  const sm = sourceMeta(st);
                  return sm ? (
                    <span key={st} title={`מקור מידע: ${sm.label}`} style={{
                      fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                      background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.35)",
                      color: "white", borderRadius: 8, padding: "2px 8px", marginLeft: 4,
                    }}>{sm.icon} {sm.label}</span>
                  ) : null;
                })}

                <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
              {onToggleSource && (hasAdvice || hasHoldings) && (() => {
                const currentClient = clients?.find(c => c.id === clientId);
                const datasets = currentClient?.datasets;
                const activeIdx = currentClient?.activeDatasetIndex ?? 0;
                const multiDataset = datasets?.length > 1;
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <div style={{ display: "inline-flex", background: "rgba(255,255,255,0.15)", borderRadius: 9, padding: 3, gap: 3 }}>
                      {[
                        { key: "holdings", label: "📦 אחזקות", enabled: hasHoldings },
                        { key: "advice", label: "📋 טרום-ייעוץ", enabled: hasAdvice },
                      ].map(({ key, label, enabled }) => {
                        const active = source === key;
                        return (
                          <button
                            key={key}
                            onClick={() => enabled && !active && onToggleSource(key)}
                            disabled={!enabled}
                            title={enabled ? `הצג ${label}` : "אין נתונים מסוג זה ללקוח"}
                            style={{
                              background: active ? "white" : "transparent",
                              color: active ? "#1e3a5f" : enabled ? "white" : "rgba(255,255,255,0.4)",
                              border: "none", borderRadius: 7, padding: "4px 12px",
                              cursor: enabled && !active ? "pointer" : "default",
                              fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                              opacity: enabled ? 1 : 0.6,
                            }}
                          >{label}</button>
                        );
                      })}
                    </div>
                    {multiDataset && source === "advice" && (
                      <div style={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ fontSize: 10, opacity: 0.7, whiteSpace: "nowrap" }}>נכונות ל:</span>
                        {datasets.map((ds, idx) => {
                          const label = ds.taarichBitzua
                            || new Date(ds.addedAt).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "2-digit" });
                          const isActive = idx === activeIdx;
                          return (
                            <button
                              key={`${ds.id}_${idx}`}
                              onClick={() => onSwitchDataset?.(clientId, idx)}
                              style={{
                                fontSize: 10, padding: "2px 7px",
                                background: isActive ? "white" : "rgba(255,255,255,0.18)",
                                color: isActive ? "#1e3a5f" : "white",
                                border: "1px solid " + (isActive ? "white" : "rgba(255,255,255,0.35)"),
                                borderRadius: 5, cursor: isActive ? "default" : "pointer",
                                fontWeight: isActive ? 700 : 400,
                              }}
                              title={ds.fileInfo?.name || ""}
                            >{label}</button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
              {!isMobile && hasHoldings && (
                <button
                  onClick={() => setShowMonthlyReturn(true)}
                  style={{
                    background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)",
                    color: "white", padding: "4px 10px", borderRadius: 8, cursor: "pointer",
                    fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                  }}
                  title="תשואה חודשית והיסטוריה"
                >📈 תשואה חודשית</button>
              )}
              {hasHoldings && source === "holdings" && monthOptions.length > 0 && (
                <select
                  value={activeMonth || ""}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.35)",
                    color: "white",
                    padding: "4px 8px", borderRadius: 8, cursor: "pointer",
                    fontSize: 12, fontWeight: 700,
                  }}
                >
                  {monthOptions.map(m => {
                    const [y, mo] = m.split("-");
                    return <option key={m} value={m} style={{ color: "#1e293b", background: "white" }}>{mo}/{y}</option>;
                  })}
                </select>
              )}

              {/* Mobile: single "⋯" menu collecting the secondary header actions
                  (they render as individual pills on desktop, gated !isMobile) */}
              {isMobile && (
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setHeaderMenuOpen(v => !v)}
                    style={{
                      background: headerMenuOpen ? "white" : "rgba(255,255,255,0.2)",
                      border: "1px solid rgba(255,255,255,0.35)",
                      color: headerMenuOpen ? "#1e3a5f" : "white",
                      padding: "4px 14px", borderRadius: 8, cursor: "pointer",
                      fontSize: 15, fontWeight: 800, lineHeight: "18px",
                    }}
                    title="פעולות נוספות"
                  >⋯</button>
                  {headerMenuOpen && (
                    <>
                      {/* invisible backdrop — closes the menu on outside tap */}
                      <div style={{ position: "fixed", inset: 0, zIndex: 140 }} onClick={() => setHeaderMenuOpen(false)} />
                      <div style={{
                        position: "absolute", top: "100%", right: 0, marginTop: 8,
                        background: "white", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        display: "flex", flexDirection: "column", gap: 4, padding: 8, zIndex: 150,
                        minWidth: 190, border: "1px solid #e2e8f0",
                      }}>
                        {[
                          hasHoldings && { icon: "📈", label: "תשואה חודשית", onClick: () => setShowMonthlyReturn(true) },
                          sentReportsCount > 0 && { icon: "🕐", label: "דוחות שנשלחו", count: sentReportsCount, onClick: () => setModal("sentReports") },
                          onSendPortalInvite && hasHoldings && { icon: "📤", label: "פורטל לקוח", onClick: onSendPortalInvite },
                          onLogActivity && { icon: "➕", label: "הוסף פעולה", onClick: () => setShowQuickLog(true) },
                          onOpenLog && { icon: "📋", label: "פעולות", count: (logEntries || []).filter(e => e.clientName === fileInfo?.client && e.type !== "note").length || null, onClick: onOpenLog },
                          { icon: "📝", label: "הערות", count: (logEntries || []).filter(e => e.clientName === fileInfo?.client && e.type === "note").length || null, onClick: () => setShowNotesModal(true) },
                          { icon: "👪", label: "משפחה", count: relations.length || null, onClick: () => setShowRelationships(true) },
                        ].filter(Boolean).map((item) => (
                          <button
                            key={item.label}
                            onClick={() => { item.onClick(); setHeaderMenuOpen(false); }}
                            style={{
                              background: "transparent", border: "none", color: "#1e293b",
                              padding: "10px 12px", borderRadius: 6, cursor: "pointer",
                              fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center",
                              gap: 8, width: "100%", textAlign: "right",
                            }}
                          >
                            <span>{item.icon}</span>
                            <span style={{ flex: 1 }}>{item.label}</span>
                            {item.count ? (
                              <span style={{
                                background: "#e2e8f0", color: "#1e3a5f", borderRadius: 10,
                                minWidth: 18, height: 18, padding: "0 5px", fontSize: 10, fontWeight: 800,
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                              }}>{item.count}</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {!isMobile && sentReportsCount > 0 && (
                <button
                  onClick={() => setModal("sentReports")}
                  style={{
                    background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)",
                    color: "white", padding: "4px 10px", borderRadius: 8, cursor: "pointer",
                    fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
                    display: "flex", alignItems: "center", gap: 4,
                  }}
                  title="היסטוריית דוחות שנשלחו ללקוח"
                >
                  🕐 דוחות
                  <span style={{
                    background: "rgba(255,255,255,0.3)", borderRadius: 8,
                    padding: "0 6px", fontSize: 10, fontWeight: 800,
                  }}>{sentReportsCount}</span>
                </button>
              )}

              {!isMobile && onSendPortalInvite && hasHoldings && (
                <button
                  onClick={onSendPortalInvite}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    color: "white", padding: "4px 10px", borderRadius: "8px",
                    cursor: "pointer", fontSize: "12px", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: "4px",
                  }}
                  title="שלח קישור פורטל ללקוח"
                >📤 פורטל לקוח</button>
              )}
              {!isMobile && onLogActivity && (
                <button
                  onClick={() => setShowQuickLog(true)}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    color: "white", padding: "4px 10px", borderRadius: "8px",
                    cursor: "pointer", fontSize: "12px", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: "4px",
                  }}
                  title="הוסף פעולה ליומן"
                >➕ פעולה</button>
              )}
              {!isMobile && onOpenLog && (
                <button
                  onClick={onOpenLog}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    color: "white", padding: "4px 10px", borderRadius: "8px",
                    cursor: "pointer", fontSize: "12px", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: "4px",
                    transition: "all 0.2s",
                  }}
                  title="יומן פעולות ללקוח זה"
                >
                  📋 פעולות
                  {logEntries && logEntries.filter(e => e.clientName === fileInfo?.client && e.type !== "note").length > 0 && (
                    <span style={{
                      background: "rgba(255,255,255,0.9)",
                      color: "#1e3a5f",
                      fontSize: 10,
                      fontWeight: 800,
                      borderRadius: 10,
                      minWidth: 18,
                      height: 18,
                      padding: "0 5px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}>
                      {logEntries.filter(e => e.clientName === fileInfo?.client && e.type !== "note").length}
                    </span>
                  )}
                </button>
              )}
              {!isMobile && (
                <button
                  onClick={() => setShowNotesModal(true)}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    color: "white", padding: "4px 10px", borderRadius: "8px",
                    cursor: "pointer", fontSize: "12px", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: "4px",
                    transition: "all 0.2s",
                  }}
                  title="הערות ללקוח זה"
                >
                  📝 הערות
                  {logEntries && logEntries.filter(e => e.clientName === fileInfo?.client && e.type === "note").length > 0 && (
                    <span style={{
                      background: "rgba(255,255,255,0.9)", color: "#1e3a5f",
                      fontSize: 10, fontWeight: 800, borderRadius: 10,
                      minWidth: 18, height: 18, padding: "0 5px",
                      display: "inline-flex", alignItems: "center",
                      justifyContent: "center", lineHeight: 1,
                    }}>
                      {logEntries.filter(e => e.clientName === fileInfo?.client && e.type === "note").length}
                    </span>
                  )}
                </button>
              )}
              {!isMobile && (
                <button
                  onClick={() => setModal("kitzbaMukeret")}
                  style={{
                    background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)",
                    color: "white", padding: "4px 10px", borderRadius: 8, cursor: "pointer",
                    fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
                  }}
                  title="מחשבון קצבה מוכרת — 106 מול נספח ב'"
                >🧾 קצבה מוכרת</button>
              )}
              {!isMobile && (
              <button
                onClick={() => setShowRelationships(true)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  color: "white", padding: "4px 10px", borderRadius: "8px",
                  cursor: "pointer", fontSize: "12px", fontWeight: 600,
                  display: "flex", alignItems: "center", gap: "4px",
                  transition: "all 0.2s",
                }}
                title="בני משפחה / קשרים בין לקוחות"
              >
                👪 משפחה
                {relations.length > 0 && (
                  <span style={{
                    background: "rgba(255,255,255,0.9)", color: "#1e3a5f",
                    fontSize: 10, fontWeight: 800, borderRadius: 10,
                    minWidth: 18, height: 18, padding: "0 5px",
                    display: "inline-flex", alignItems: "center",
                    justifyContent: "center", lineHeight: 1,
                  }}>
                    {relations.length}
                  </span>
                )}
              </button>
              )}
              {!isCompareMode ? (!isMobile && (
                <button
                  onClick={onOpenSplit}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                    padding: "4px 10px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "all 0.2s"
                  }}
                  className="btn-info"
                >
                  📁 מסך מפוצל
                </button>
              )) : (
                <button
                  onClick={onCloseSplit}
                  style={{
                    background: "#ef4444",
                    border: "none",
                    color: "white",
                    padding: "4px 10px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "all 0.2s"
                  }}
                >
                  ✕ סגור
                </button>
              )}
                </div>
              </div>
            </div>
            {onGoHome && (
              <button
                onClick={onGoHome}
                style={{
                  background: "rgba(255,255,255,0.18)", border: "2px solid rgba(255,255,255,0.45)",
                  color: "white", padding: isMobile ? "6px 12px" : "10px 20px",
                  borderRadius: isMobile ? 10 : 12, cursor: "pointer",
                  fontSize: isMobile ? 18 : 26, fontWeight: 800, whiteSpace: "nowrap", flexShrink: 0,
                  display: "flex", alignItems: "center", lineHeight: 1,
                }}
                title="חזרה למסך הבית"
              >🏠</button>
            )}
          </div>
        </div>
      </div>

      <div style={{ padding: isCompareMode ? "12px 12px 0" : isMobile ? "12px 10px 0" : "20px 20px 0" }}>

        {/* Unified Top Strip: Summary Cards + Actions */}
        <div style={{
          display: "flex", flexDirection: isMobile ? "column" : "row",
          alignItems: "stretch", gap: isMobile ? 8 : 12, marginBottom: isMobile ? 12 : 20,
          background: "white", borderRadius: 16, padding: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          border: "1px solid #e2e8f0"
        }}>
          {/* Scrollable Summary Cards */}
          <div style={{
            display: "flex", gap: 10, overflowX: "auto", flex: 1, minWidth: 0, padding: "4px 0",
            scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch"
          }}>
            <div style={{ display: "flex", gap: 10, minWidth: "max-content" }}>
              <SummaryCard label="סה״כ ערך פדיון" value={fmtMoney(totals.redeem)}
                borderColor="#4338ca" condensed />
              <SummaryCard label="קצבה חודשית צפויה" value={fmtMoney(totals.pension)}
                borderColor="#16a34a" condensed />
              <SummaryCard label="כיסוי ביטוחי כולל" value={fmtMoney(totals.bituach)}
                sub="לחץ לריכוז כיסויים"
                borderColor="#e11d48" condensed onClick={() => setModal("kisuim")} />
              <SummaryCard label="התראות"
                value={totals.alerts ? `${totals.alerts} התראות` : "✅ תקין"}
                sub={totals.alerts ? "פיגורים / שעבודים / הלוואות" : undefined}
                borderColor={totals.alerts ? "#dc2626" : "#16a34a"} condensed
                onClick={() => setModal("alerts")} />
              {/* Separator */}
              <div style={{ width: 1, background: "#e2e8f0", margin: "4px 6px", alignSelf: "stretch", flexShrink: 0 }} />
              <SummaryCard label="סה״כ בהון" value={fmtMoney(totals.tagmulimHon)}
                borderColor="#2563eb" condensed onClick={() => setModal("hon")} />
              <SummaryCard label="סה״כ בקצבה" value={fmtMoney(totals.tagmulimKitzba + totals.pitzuyimKitzba)}
                borderColor="#16a34a" condensed onClick={() => setModal("kitzba")} />
              <SummaryCard label="ריכוז פיצויים" value={fmtMoney(totals.pitzuyimTotal)}
                borderColor="#d97706" condensed onClick={() => setModal("pitzuyim")} />
              <SummaryCard label="ריכוז תגמולים" value={fmtMoney(totals.tagmulimTotal)}
                borderColor="#7c3aed" condensed onClick={() => setModal("tagmulim")} />
              <SummaryCard label="צבע כסף" value="לפי מעסיק"
                borderColor="#7c3aed" condensed onClick={() => setModal("moneyColor")} />
            </div>
          </div>

          {/* Action Column (desktop) / compact action row (mobile) */}
          <div style={isMobile
            ? { padding: "6px 4px 2px", borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "stretch", gap: 6 }
            : { padding: "4px", borderRight: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => openModal("snapshot")} style={isMobile ? {
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              fontSize: 11, fontWeight: 800, padding: "8px 6px", flex: 1,
              background: "white", color: "#7c3aed",
              border: "1.5px solid #7c3aed", borderRadius: 10, cursor: "pointer",
            } : {
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
              fontSize: 11, fontWeight: 800, padding: "10px 14px", width: 95, height: "100%",
              background: "white", color: "#7c3aed",
              border: "1.5px solid #7c3aed", borderRadius: 12,
              cursor: "pointer", transition: "all 0.15s",
              boxShadow: "0 1px 3px rgba(124, 58, 237, 0.1)", flexShrink: 0
            }}>
              <span style={{ fontSize: isMobile ? 15 : 20 }}>🖼️</span>
              <span>תמונת לקוח</span>
            </button>
            {retirementPending && <style>{RETIREMENT_BTN_STYLES}</style>}
            <button onClick={handleRetirementClick} disabled={retirementPending} style={isMobile ? {
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              fontSize: 11, fontWeight: 800, padding: "8px 6px", flex: 1,
              background: retirementPending ? "#f0fdf4" : "white", color: "#059669",
              border: "1.5px solid #059669", borderRadius: 10,
              cursor: retirementPending ? "default" : "pointer",
              animation: retirementPending ? "rp-glow 1.4s ease-in-out infinite" : "none",
            } : {
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
              fontSize: 11, fontWeight: 800, padding: "10px 14px", width: 95, height: "100%",
              background: retirementPending ? "#f0fdf4" : "white", color: "#059669",
              border: `1.5px solid #059669`, borderRadius: 12,
              cursor: retirementPending ? "default" : "pointer", transition: "background 0.2s",
              boxShadow: "0 1px 3px rgba(5, 150, 105, 0.1)", flexShrink: 0,
              animation: retirementPending ? "rp-glow 1.4s ease-in-out infinite" : "none",
            }}>
              {retirementPending ? <RetirementSpinner /> : <span style={{ fontSize: isMobile ? 15 : 20 }}>🏖️</span>}
              <span>{retirementPending ? "טוען..." : "תכנון פרישה"}</span>
            </button>
          </div>
        </div>

        <PortfolioExposure funds={fundsForView} />

        {/* Charts toggle — text line right above the graphs */}
        <div
          onClick={() => setShowCharts(v => !v)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
            fontSize: 13, fontWeight: 700, color: "#1e40af", marginBottom: 10,
            userSelect: "none",
          }}
        >
          <span style={{ fontSize: 15 }}>{showCharts ? "📋" : "📊"}</span>
          <span>{showCharts ? "הסתר גרפים ▲" : "הצג גרפים ▼"}</span>
        </div>

        {/* Charts panel */}
        {showCharts && (
          <Suspense fallback={<ModalSpinner minHeight={400} />}>
            <DashboardCharts funds={fundsForView} onAlertsClick={() => setModal("alerts")} history={history} />
          </Suspense>
        )}


        {/* Inline retirement planning (compare mode) */}
        {isCompareMode && retirementView ? (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
              <button onClick={() => { setRetirementView(false); setRetirementPending(false); }} style={{
                fontSize: 12, padding: "6px 14px",
                background: "white", border: "1.5px solid #e2e8f0",
                borderRadius: 8, cursor: "pointer", color: "#475569",
                display: "flex", alignItems: "center", gap: 4,
              }}>← חזור לקרנות</button>
            </div>
            <Suspense fallback={<ModalSpinner minHeight={400} />}>
              <RetirementPlanning key={plan.planKey} {...retirementProps} inline={true} />
            </Suspense>
          </div>
        ) : (
          <div>
            {/* Tabs for Dashboard */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16, borderBottom: "1px solid #e2e8f0" }}>
              <button onClick={() => setActiveTab("holdings")} style={{
                padding: "10px 16px", cursor: "pointer", background: "none", border: "none",
                borderBottom: activeTab === "holdings" ? "3px solid #2563eb" : "3px solid transparent",
                fontWeight: activeTab === "holdings" ? 800 : 600,
                color: activeTab === "holdings" ? "#1e3a5f" : "#64748b",
                fontSize: 15, transition: "all 0.2s"
              }}>📦 חיסכון פיננסי ופנסיוני</button>
              <button onClick={() => setActiveTab("insurance")} style={{
                padding: "10px 16px", cursor: "pointer", background: "none", border: "none",
                borderBottom: activeTab === "insurance" ? "3px solid #6d28d9" : "3px solid transparent",
                fontWeight: activeTab === "insurance" ? 800 : 600,
                color: activeTab === "insurance" ? "#6d28d9" : "#64748b",
                fontSize: 15, transition: "all 0.2s"
              }}>🛡️ ביטוח חיים ובריאות</button>
            </div>

            {/* Search + filters */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="🔍  חיפוש חברה / מוצר / מספר / שם לקוח..."
                style={{
                  flex: 1, minWidth: 180, padding: "8px 12px",
                  border: "1.5px solid #e2e8f0", borderRadius: 10,
                  fontSize: isCompareMode ? 12 : 13, outline: "none", direction: "rtl", background: "white",
                }}
              />
              {statuses.map(s => (
                <button key={s} onClick={() => setFilterStatus(s)} style={{
                  padding: "6px 12px", borderRadius: 20, fontSize: 11, cursor: "pointer",
                  fontWeight: filterStatus === s ? 700 : 400,
                  background: filterStatus === s ? "#4338ca" : "white",
                  border: `1.5px solid ${filterStatus === s ? "#4338ca" : "#e2e8f0"}`,
                  color: filterStatus === s ? "white" : "#475569",
                }}>{s}</button>
              ))}
            </div>

            {/* Month-filter banner — shown for any selected month (008 + פרט) */}
            {activeMonth && (
              <div style={{
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10,
                padding: "8px 14px", marginBottom: 10, fontSize: 12, color: "#92400e",
              }}>
                <span style={{ fontWeight: 800 }}>
                  📅 מציג נתוני חודש {activeMonth.split("-").reverse().join("/")}
                </span>
                <span>· שווי תיק {fmtMoney(fundsForView.reduce((s, f) => s + (f.redeem || 0), 0))}</span>
                <span style={{ color: "#b45309" }}>· כל האחזקות והפירוט מתייחסים לחודש זה</span>
                {activeMonth !== monthOptions[0] && (
                <button
                  onClick={() => setSelectedMonth(monthOptions[0])}
                  style={{
                    padding: "6px 12px", background: "#f1f5f9", border: "none", borderRadius: 6,
                    color: "#475569", cursor: "pointer", fontSize: 12, fontWeight: 600
                  }}
                >חזרה לחודש אחרון ✕</button>
              )}</div>
            )}

            {/* Funds table */}
            <div style={{ paddingBottom: 40 }}>
              <FundsTable
            funds={filtered}
            clientId={clientId}
            totalCount={fundsForView.length}
            activeTab={activeTab}
            onAddProduct={source === "holdings" && onAddManualFund ? () => setShowManualModal(true) : undefined}
            onRemoveFund={onRemoveFund}
          />
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────── */}
      {modal === "pitzuyim" && (
        <PitzuyimModal funds={funds} onClose={() => setModal(null)} inline={isCompareMode} clientName={fileInfo?.client} />
      )}
      {modal === "161" && (
        <Suspense fallback={<ModalSpinner />}>
          <Form161Modal
            funds={funds} onClose={() => { setModal(returnModal); setReturnModal(null); }}
            savedData={form161Data} onDataChange={setForm161Data}
            planningImport={retirementData} inline={isCompareMode}
            onOpen116={() => setModal("116")} form116Data={form116Data}
            clientInfo={aggregatedClient}
          />
        </Suspense>
      )}
      {modal === "116" && (
        <Suspense fallback={<ModalSpinner />}>
          <Form116Modal
            form161Data={form161Data} retirementData={retirementData}
            clientInfo={aggregatedClient} savedData={form116Data}
            onDataChange={setForm116Data} onClose={() => setModal(null)}
            inline={isCompareMode}
          />
        </Suspense>
      )}
      {modal === "kivuZchuyot" && (
        <Suspense fallback={<ModalSpinner />}>
          <KivuZchuyotModal
            funds={funds} onClose={() => { setModal(returnModal); setReturnModal(null); }}
            savedData={kivuData} onDataChange={setKivuData}
            planningImport={retirementData} inline={isCompareMode}
          />
        </Suspense>
      )}
      {modal === "kitzbaMukeret" && (
        <Suspense fallback={<ModalSpinner />}>
          <KitzbaMukeret clientId={clientTaz} clientName={fileInfo?.client} onClose={() => setModal(null)} />
        </Suspense>
      )}
      {modal === "tagmulim" && <TagmulimModal funds={funds} onClose={() => setModal(null)} inline={isCompareMode} clientName={fileInfo?.client} />}
      {modal === "retirement" && !isCompareMode && (
        <Suspense fallback={<ModalSpinner />}>
          <RetirementPlanning key={plan.planKey} {...retirementProps} onClose={() => { setModal(null); setRetirementPending(false); }} inline={false} />
        </Suspense>
      )}
      {modal === "snapshot" && (
        <CustomerSnapshot funds={funds} clientId={clientId} clientName={fileInfo?.client}
          onClose={() => setModal(null)} inline={isCompareMode} />
      )}
      {modal === "sentReports" && (
        <ClientReportHistoryModal
          clientId={clientTaz}
          clientName={fileInfo?.client}
          snapshots={history}
          liveFunds={funds}
          onClose={() => setModal(null)}
        />
      )}
      {showManualModal && (
        <ManualProductModal
          clientId={clientId}
          clientName={fileInfo?.client}
          funds={funds}
          onAdd={onAddManualFund}
          onUpdate={onUpdateManualFund}
          onRemove={onRemoveManualFund}
          onLogActivity={onLogActivity}
          onClose={() => setShowManualModal(false)}
        />
      )}
      {modal === "kisuim" && <KisuimModal funds={funds} onClose={() => setModal(null)} inline={isCompareMode} clientName={fileInfo?.client} />}
      {modal === "hon"    && <HonModal    funds={funds} onClose={() => setModal(null)} inline={isCompareMode} clientName={fileInfo?.client} />}
      {modal === "kitzba" && <KitzbaModal funds={funds} onClose={() => setModal(null)} inline={isCompareMode} clientName={fileInfo?.client} />}
      {modal === "moneyColor" && <MoneyColorModal funds={funds} onClose={() => setModal(null)} inline={isCompareMode} clientName={fileInfo?.client} />}
      {modal === "clientDetails" && (
        <ClientDetailsModal
          client={aggregatedClientBase} employer={aggregatedEmployer} agent={aggregatedAgent}
          onSaved={() => setOverridesVersion(v => v + 1)}
          onClose={() => setModal(null)} inline={isCompareMode} />
      )}
      {modal === "alerts" && <AlertsModal funds={funds} onClose={() => setModal(null)} inline={isCompareMode} />}
      {showQuickLog && (
        <QuickLogModal
          clientName={fileInfo?.client}
          onAdd={onLogActivity}
          onClose={() => setShowQuickLog(false)}
        />
      )}
      {showRelationships && (
        <RelationshipsModal
          clientId={clientId}
          clients={clients}
          onSelectClient={onSelectClient || (() => {})}
          onClose={() => setShowRelationships(false)}
        />
      )}
      {showMonthlyReturn && (
        <MonthlyReturnPanel
          clientId={fileInfo?.clientId}
          clientName={fileInfo?.client}
          extraSnapshots={proprietaryHistory}
          onClose={() => setShowMonthlyReturn(false)}
        />
      )}

      {showNotesModal && (
        <ClientNotesModal
          entries={(logEntries || []).filter(e => e.clientName === fileInfo?.client)}
          onAdd={onLogActivity}
          onUpdate={onUpdateLog}
          onDelete={onDeleteLog}
          onClose={() => setShowNotesModal(false)}
          clientName={fileInfo?.client}
        />
      )}

    </div>
  );
}

function ReportTiles({ funds, totals, isCompareMode, gridCols, onHon, onKitzba, onPitzuyim, onTagmulim }) {
  const pitzuyimTotal = funds.filter(f => f.yitrot?.pitzuyim?.erkeiPitzuimMaasik || f.yitrot?.perut?.some(p => p.sugHafrasha === "פיצויים")).reduce((s, f) => {
    const v = f.yitrot?.pitzuyim?.erkeiPitzuimMaasik || f.yitrot?.perut?.filter(p => p.sugHafrasha === "פיצויים").reduce((a, p) => a + (p.redeem || 0), 0) || 0;
    return s + v;
  }, 0);

  const tagmulimTotal = funds
    .filter(f => f.cat !== "קרן השתלמות")
    .reduce((s, f) => s + (f.yitrot?.perut || [])
      .filter(p => p.sugHafrasha !== "פיצויים")
      .reduce((a, p) => a + (p.redeem || 0), 0), 0);

  const tiles = [
    { label: "סה״כ בהון", value: fmtMoney(totals.tagmulimHon), icon: "💰", border: "#bfdbfe", color: "#2563eb", onClick: onHon },
    { label: "סה״כ בקצבה", value: fmtMoney(totals.tagmulimKitzba + totals.pitzuyimKitzba), icon: "📅", border: "#bbf7d0", color: "#16a34a", onClick: onKitzba },
    { label: "ריכוז יתרות פיצויים", value: fmtMoney(pitzuyimTotal), icon: "💼", border: "#fde68a", color: "#d97706", sub: "לחץ לדוח מפורט + ייצוא CSV", onClick: onPitzuyim },
    { label: "ריכוז תגמולים", value: fmtMoney(tagmulimTotal), icon: "📊", border: "#bfdbfe", color: "#2563eb", sub: "קופה משלמת / לא משלמת / הון + ייצוא CSV", onClick: onTagmulim },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 8, marginBottom: 16 }}>
      {tiles.map((t, i) => (
        <div key={i} onClick={t.onClick} style={{
          background: "white", border: `1.5px solid ${t.border}`, borderRadius: 12,
          padding: isCompareMode ? "8px 10px" : "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12,
          transition: "all 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}>
          <div style={{ fontSize: isCompareMode ? 24 : 32 }}>{t.icon}</div>
          <div>
            <div style={{ fontSize: isCompareMode ? 14 : 17, color: t.color }}>{t.label}</div>
            <div style={{ fontSize: isCompareMode ? 14 : 18, color: "#0f172a", marginTop: 4 }}>{t.value}</div>
            {!isCompareMode && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{t.sub || "לחץ לפירוט"}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
